/**
 * Сервис синхронизации событий с Backend
 * Интегрирован с NetworkManager для корректной работы в офлайн-режиме
 * 
 * ИЗМЕНЕНО: Использует SyncQueue для синхронизации сотрудников
 * вместо прямого вызова EmployeesService для предотвращения race condition
 */
import { EventsRepo } from '../offline/eventsRepo'
import { LogsRepo } from '../offline/logsRepo'
import { apiClient } from './apiClient'
import { useDeviceStore } from '../store/deviceStore'
import { NetworkManager } from './networkManager'
import { SyncQueue } from './syncQueue'

export interface EventBatchItem {
  id: string
  employee_id: string
  event_type: 'check_in' | 'check_out'
  source: 'scan' | 'manual'
  happened_at: string
  payload_hash: string
  qr_token?: string
}

export interface SyncResult {
  success: boolean
  saved: number
  duplicates: number
  errors: number
  pending: number
}

export type SyncStatus = 'idle' | 'syncing' | 'success' | 'error' | 'offline'

// Global sync status listeners
const syncStatusListeners = new Set<(status: SyncStatus, result?: SyncResult) => void>()

export function subscribeSyncStatus(listener: (status: SyncStatus, result?: SyncResult) => void): () => void {
  syncStatusListeners.add(listener)
  return () => syncStatusListeners.delete(listener)
}

function notifySyncStatus(status: SyncStatus, result?: SyncResult) {
  syncStatusListeners.forEach(listener => {
    try {
      listener(status, result)
    } catch (e) {
      console.error('[SyncService] Listener error:', e)
    }
  })
}

export class SyncService {
  private static lastSyncResult: SyncResult | null = null
  private static currentStatus: SyncStatus = 'idle'

  /**
   * Получает текущий статус синхронизации
   */
  static getStatus(): SyncStatus {
    return this.currentStatus
  }

  /**
   * Получает последний результат синхронизации
   */
  static getLastResult(): SyncResult | null {
    return this.lastSyncResult
  }

  /**
   * Синхронизирует несинхронизированные события с Backend
   */
  static async syncEvents(): Promise<SyncResult> {
    const deviceStore = useDeviceStore.getState()
    
    // Детальное логирование состояния устройства
    console.log('[SyncService] 📋 Device state:', {
      deviceId: deviceStore.deviceId,
      siteId: deviceStore.siteId,
      deviceSecret: deviceStore.deviceSecret ? '***set***' : 'NOT SET',
      isRegistered: deviceStore.isRegistered
    })
    
    if (!deviceStore.deviceId || !deviceStore.siteId) {
      const warnMsg = `Cannot sync: device not registered (deviceId: ${deviceStore.deviceId ? 'set' : 'missing'}, siteId: ${deviceStore.siteId ? 'set' : 'missing'})`
      await LogsRepo.save('warn', warnMsg)
      console.warn(`[SyncService] ⚠️ ${warnMsg}`)
      this.currentStatus = 'error'
      notifySyncStatus('error')
      return {
        success: false,
        saved: 0,
        duplicates: 0,
        errors: 0,
        pending: 0,
      }
    }

    if (!deviceStore.deviceSecret) {
      const warnMsg = 'Cannot sync: device secret not set'
      await LogsRepo.save('warn', warnMsg)
      console.warn(`[SyncService] ⚠️ ${warnMsg}`)
      this.currentStatus = 'error'
      notifySyncStatus('error')
      return {
        success: false,
        saved: 0,
        duplicates: 0,
        errors: 0,
        pending: 0,
      }
    }

    // Проверяем статус сети - если офлайн, не пытаемся синхронизировать
    const networkStatus = NetworkManager.getStatus()
    if (!NetworkManager.isOnline()) {
      // Логируем только если статус checking (чтобы не спамить при офлайне)
      if (networkStatus === 'checking') {
        console.log(`[SyncService] ⏳ Network status: ${networkStatus}, skipping sync`)
      }
      this.currentStatus = 'offline'
      notifySyncStatus('offline')
      return {
        success: false,
        saved: 0,
        duplicates: 0,
        errors: 0,
        pending: await EventsRepo.getUnsyncedCount(),
      }
    }
    
    this.currentStatus = 'syncing'
    notifySyncStatus('syncing')
    console.log(`[SyncService] 🔄 Starting sync (network: ${networkStatus})`)

    // Получаем несинхронизированные события (максимум 100)
    const allUnsyncedEvents = await EventsRepo.getUnsynced(100)
    
    // Фильтруем события по текущему site_id (на случай если остались старые события)
    const unsyncedEvents = allUnsyncedEvents.filter(
      (event) => event.site_id === deviceStore.siteId
    )
    
    // Если были отфильтрованы события, помечаем их как синхронизированные
    if (allUnsyncedEvents.length !== unsyncedEvents.length) {
      const filteredCount = allUnsyncedEvents.length - unsyncedEvents.length
      console.warn(`[SyncService] ⚠️ Filtered out ${filteredCount} events with wrong site_id`)
      for (const event of allUnsyncedEvents) {
        if (event.site_id !== deviceStore.siteId) {
          await EventsRepo.markSynced(event.id)
        }
      }
    }
    
    // Также проверяем failed события (для повторной попытки)
    const allFailedEvents = await EventsRepo.getFailed(10) // Берем только 10 для повторной попытки
    
    // Фильтруем failed события по текущему site_id
    const failedEvents = allFailedEvents.filter(
      (event) => event.site_id === deviceStore.siteId
    )
    
    // Если были отфильтрованы failed события, помечаем их как синхронизированные
    if (allFailedEvents.length !== failedEvents.length) {
      const filteredCount = allFailedEvents.length - failedEvents.length
      console.warn(`[SyncService] ⚠️ Filtered out ${filteredCount} failed events with wrong site_id`)
      for (const event of allFailedEvents) {
        if (event.site_id !== deviceStore.siteId) {
          await EventsRepo.markSynced(event.id)
        }
      }
    }
    
    const totalEventsToSync = unsyncedEvents.length + failedEvents.length
    
    if (totalEventsToSync === 0) {
      console.log(`[SyncService] ✅ No unsynced events`)
      const result: SyncResult = {
        success: true,
        saved: 0,
        duplicates: 0,
        errors: 0,
        pending: 0,
      }
      this.lastSyncResult = result
      this.currentStatus = 'success'
      notifySyncStatus('success', result)
      return result
    }
    
    // Сбрасываем статус failed для повторной попытки
    for (const failedEvent of failedEvents) {
      await EventsRepo.resetFailedStatus(failedEvent.id)
      console.log(`[SyncService] 🔄 Retrying failed event: ${failedEvent.id}`)
    }
    
    // Объединяем события для синхронизации
    const allEventsToSync = [...unsyncedEvents, ...failedEvents]
    
    console.log(`[SyncService] 📦 Found ${unsyncedEvents.length} unsynced + ${failedEvents.length} failed = ${allEventsToSync.length} total events to sync`)

    // Формируем батч для отправки с валидацией UUID
    const batch: EventBatchItem[] = []
    const invalidEvents: string[] = []
    
    for (const event of allEventsToSync) {
      // Проверяем валидность UUID перед добавлением в батч
      if (!EventsRepo.isValidUUID(event.employee_id)) {
        console.warn(`[SyncService] ⚠️ Invalid employee_id UUID: ${event.employee_id} (event ${event.id})`)
        invalidEvents.push(event.id)
        // Помечаем событие как синхронизированное (чтобы не пытаться снова)
        await EventsRepo.markSynced(event.id)
        continue
      }
      
      // Проверяем что событие относится к текущему site_id
      if (event.site_id !== deviceStore.siteId) {
        console.warn(`[SyncService] ⚠️ Event site_id mismatch: ${event.site_id} !== ${deviceStore.siteId} (event ${event.id})`)
        invalidEvents.push(event.id)
        // Помечаем событие как синхронизированное (чтобы не пытаться снова)
        await EventsRepo.markSynced(event.id)
        continue
      }
      
      batch.push({
        id: event.id,
        employee_id: event.employee_id,
        event_type: event.event_type,
        source: event.source,
        happened_at: event.happened_at,
        payload_hash: event.payload_hash,
        qr_token: event.qr_token,
      })
    }
    
    // Если все события были невалидными, возвращаем результат
    if (batch.length === 0) {
      if (invalidEvents.length > 0) {
        console.warn(`[SyncService] ⚠️ All ${invalidEvents.length} events were invalid and skipped`)
        await LogsRepo.save('warn', `Skipped ${invalidEvents.length} invalid events during sync`)
      }
      const result: SyncResult = {
        success: true,
        saved: 0,
        duplicates: 0,
        errors: 0,
        pending: await EventsRepo.getUnsyncedCount(),
      }
      this.lastSyncResult = result
      this.currentStatus = 'success'
      notifySyncStatus('success', result)
      return result
    }

    try {
      // Отправляем батч на сервер
      const response = await apiClient.post<{
        success: boolean
        data: {
          saved_ids: string[]
          duplicates: string[]
          errors: Array<{ local_id: string; error: string; message: string }>
        }
      }>('/tablet/events/batch', {
        device_id: deviceStore.deviceId,
        site_id: deviceStore.siteId,
        events: batch,
      })

      if (!response.success) {
        // Извлекаем детали ошибки из ответа
        const errorDetail = (response as any).error?.message || (response as any).detail || 'Sync failed'
        throw new Error(errorDetail)
      }

      const { saved_ids, duplicates, errors } = response.data

      // Помечаем успешно синхронизированные события
      for (const id of saved_ids) {
        await EventsRepo.markSynced(id)
      }

      // Дубликаты тоже помечаем как синхронизированные (они уже на сервере)
      for (const id of duplicates) {
        await EventsRepo.markSynced(id)
      }

      // Обрабатываем ошибки событий
      for (const error of errors) {
        // Проверяем тип ошибки
        const isExpiredToken = error.message.includes('истёк') || error.message.includes('невалиден')
        
        if (isExpiredToken) {
          // Для истекших токенов помечаем событие как синхронизированное
          // (нет смысла повторять с истекшим токеном)
          await EventsRepo.markSynced(error.local_id)
          const warnMsg = `Event ${error.local_id}: Token expired (marked as synced)`
          await LogsRepo.save('warn', warnMsg)
          console.warn(`[SyncService] ⚠️ ${warnMsg}`)
        } else {
          // Для других ошибок увеличиваем счётчик попыток
        await EventsRepo.incrementSyncAttempts(error.local_id)
          const errorMsg = `Sync error for event ${error.local_id}: ${error.message}`
          await LogsRepo.save('error', errorMsg)
          console.error(`[SyncService] ❌ ${errorMsg}`)
        }
      }

      const syncSummary = `Synced ${saved_ids.length} events, ${duplicates.length} duplicates, ${errors.length} errors`
      await LogsRepo.save('info', syncSummary)
      
      // Логируем результат синхронизации в консоль
      if (errors.length > 0) {
        console.warn(`[SyncService] ⚠️ ${syncSummary}`)
      } else {
        console.log(`[SyncService] ✅ ${syncSummary}`)
      }

      // Возвращаем success=true только если нет ошибок
      const allSuccess = errors.length === 0
      const pendingCount = await EventsRepo.getUnsyncedCount()

      const result: SyncResult = {
        success: allSuccess,
        saved: saved_ids.length,
        duplicates: duplicates.length,
        errors: errors.length,
        pending: pendingCount,
      }
      
      this.lastSyncResult = result
      this.currentStatus = allSuccess ? 'success' : 'error'
      notifySyncStatus(this.currentStatus, result)
      
      return result
    } catch (error) {
      // Проверяем, является ли ошибка сетевой
      const isNetworkError = error instanceof Error && 
        (error.name === 'NetworkError' || 
         error.message.includes('Failed to fetch') ||
         error.message.includes('интернету'))

      // Если сетевая ошибка, НЕ увеличиваем счетчик попыток и НЕ логируем
      // NetworkManager уже показывает индикатор офлайна
      if (isNetworkError) {
        this.currentStatus = 'offline'
        notifySyncStatus('offline')
        return {
          success: false,
          saved: 0,
          duplicates: 0,
          errors: 0,
          pending: await EventsRepo.getUnsyncedCount(),
        }
      }

      // Для критических ошибок (весь батч провалился) увеличиваем счётчик для всех событий в батче
      // Это произойдет ТОЛЬКО если сервер вообще не ответил или вернул 500
      for (const event of allEventsToSync) {
        await EventsRepo.incrementSyncAttempts(event.id)
      }

      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      const errorLog = `Sync failed completely: ${errorMessage}`
      await LogsRepo.save('error', errorLog)
      // Логируем критическую ошибку в консоль
      console.error(`[SyncService] ❌ ${errorLog}`, error)

      const pendingCount = await EventsRepo.getUnsyncedCount()
      const result: SyncResult = {
        success: false,
        saved: 0,
        duplicates: 0,
        errors: allEventsToSync.length,
        pending: pendingCount,
      }
      
      this.lastSyncResult = result
      this.currentStatus = 'error'
      notifySyncStatus('error', result)
      
      return result
    }
  }

  /**
   * Запускает периодическую синхронизацию
   * 
   * ИЗМЕНЕНО: 
   * - Интервал увеличен с 30 до 60 секунд (снижение нагрузки)
   * - Использует SyncQueue для синхронизации сотрудников (предотвращение race condition)
   * - NORMAL приоритет = с debounce, не чаще 1 раза в 5 секунд
   */
  static startPeriodicSync(intervalMs: number = 60000): () => void {
    let intervalId: number | null = null
    let isRunning = false // Флаг для предотвращения параллельных синхронизаций

    const sync = async () => {
      // Проверяем сеть перед попыткой синхронизации
      if (!NetworkManager.isOnline()) {
        console.log('[SyncService] ⏸️ Network offline, skipping periodic sync')
        return
      }
      
      // Предотвращаем параллельные синхронизации событий
      if (isRunning) {
        console.log('[SyncService] ⏸️ Sync already in progress, skipping...')
        return
      }
      
      isRunning = true
      try {
        // Синхронизируем события
        await SyncService.syncEvents()
        
        // Синхронизируем данные сотрудников через SyncQueue
        // NORMAL приоритет = с debounce, предотвращает частые запросы
        try {
          await SyncQueue.requestSync('NORMAL')
          console.log('[SyncService] ✅ Employee data synchronized via SyncQueue')
        } catch (empError) {
          console.error('[SyncService] ⚠️ Failed to sync employee data:', empError)
          // Не прерываем синхронизацию если не удалось обновить сотрудников
        }
      } catch (error) {
        console.error('[SyncService] ❌ Periodic sync error:', error)
        await LogsRepo.save('error', `Periodic sync error: ${error instanceof Error ? error.message : 'Unknown error'}`)
      } finally {
        isRunning = false
      }
    }

    // Синхронизируем сразу при старте
    sync()

    // Затем периодически (каждые 60 секунд по умолчанию)
    intervalId = window.setInterval(sync, intervalMs)
    console.log(`[SyncService] ✅ Periodic sync started (interval: ${intervalMs}ms)`)

    // Возвращаем функцию для остановки
    return () => {
      if (intervalId !== null) {
        clearInterval(intervalId)
        console.log('[SyncService] 🛑 Periodic sync stopped')
      }
    }
  }

  /**
   * Выполняет синхронизацию немедленно
   */
  static async syncNow(): Promise<SyncResult> {
    return await SyncService.syncEvents()
  }
}

